import React, { useMemo, useState } from "react";
import { auth } from "../firebase";
import { colors, radius, spacing, typography } from "../utils/designTokens";
import { useUser } from "../context/UserContext";
import { useToast } from "./Toast";
import { loadContacto } from "../screens/comercial/materialesUi";
import { plantillaWhatsappAdmin, plantillaMailAdmin } from "../utils/plantillasAdministraciones";
import { FaWhatsapp, FaRegEnvelope, FaRegCopy, FaCheck, FaExternalLinkAlt, FaRegFilePdf } from "react-icons/fa";

// El material de los planes es un PDF fijo servido desde /public (mismo que en Materiales).
const PLANES_PDF = "/materiales/planes-administraciones.pdf";

const digitosWa = (tel) => {
  const d = String(tel || "").replace(/\D/g, "");
  if (!d) return "";
  return d.length <= 9 ? `598${d}` : d;
};

/**
 * Panel de primer contacto con una administración (canal B2B): arma el mensaje
 * (WhatsApp o mail) con la plantilla + el nombre de la administración y del
 * agente logueado, para copiar/enviar, y acompaña el material de los planes
 * (PDF) para adjuntar/compartir.
 */
export default function ContactoAdministracion({ admin }) {
  const { showToast } = useToast();
  const { userName, userEmail } = useUser();
  const email = (userEmail || auth.currentUser?.email || "").toLowerCase();
  const c0 = loadContacto(email);
  const agenteNombre = c0.name || userName || "el equipo de Habitia";

  const [canal, setCanal] = useState("wsp");
  const [copiado, setCopiado] = useState("");

  const administracion = (admin?.nombre || "").trim() || "el equipo";
  const vars = { administracion, agente: agenteNombre };
  const wspText = useMemo(() => plantillaWhatsappAdmin(vars), [administracion, agenteNombre]); // eslint-disable-line
  const mail = useMemo(() => plantillaMailAdmin(vars), [administracion, agenteNombre]); // eslint-disable-line

  const telWa = digitosWa(admin?.contactoTel || admin?.telefono);
  const mailTo = (admin?.contactoEmail || admin?.email || "").trim();

  async function copiar(texto, cual) {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(cual);
      setTimeout(() => setCopiado((v) => (v === cual ? "" : v)), 2000);
    } catch { showToast("No se pudo copiar.", "error"); }
  }
  function abrirWhatsapp() {
    if (!telWa) { showToast("La administración no tiene teléfono cargado.", "error"); return; }
    window.open(`https://wa.me/${telWa}?text=${encodeURIComponent(wspText)}`, "_blank", "noopener");
  }
  function abrirMail() {
    if (!mailTo) { showToast("La administración no tiene email cargado.", "error"); return; }
    window.location.href = `mailto:${mailTo}?subject=${encodeURIComponent(mail.asunto)}&body=${encodeURIComponent(mail.cuerpo)}`;
  }

  return (
    <div style={box}>
      <div style={{ display: "flex", gap: spacing[2], marginBottom: spacing[3] }}>
        <button type="button" onClick={() => setCanal("wsp")} style={tabBtn(canal === "wsp")}><FaWhatsapp size={13} /> WhatsApp</button>
        <button type="button" onClick={() => setCanal("mail")} style={tabBtn(canal === "mail")}><FaRegEnvelope size={13} /> Mail</button>
      </div>

      {canal === "wsp" ? (
        <>
          <label style={lbl}>Mensaje de WhatsApp</label>
          <div style={preview}>{wspText}</div>
          <div style={acciones}>
            <button type="button" onClick={() => copiar(wspText, "wsp")} style={btnPri}>{copiado === "wsp" ? <FaCheck size={12} /> : <FaRegCopy size={12} />} {copiado === "wsp" ? "Copiado" : "Copiar mensaje"}</button>
            <button type="button" onClick={abrirWhatsapp} style={btnSec}><FaExternalLinkAlt size={11} /> Abrir WhatsApp{telWa ? "" : " (falta tel.)"}</button>
          </div>
        </>
      ) : (
        <>
          <label style={lbl}>Asunto</label>
          <div style={{ ...preview, marginBottom: spacing[2] }}>{mail.asunto}</div>
          <label style={lbl}>Cuerpo del mail</label>
          <div style={preview}>{mail.cuerpo}</div>
          <div style={acciones}>
            <button type="button" onClick={() => copiar(mail.asunto, "asunto")} style={btnSec}>{copiado === "asunto" ? <FaCheck size={12} /> : <FaRegCopy size={12} />} Asunto</button>
            <button type="button" onClick={() => copiar(mail.cuerpo, "cuerpo")} style={btnPri}>{copiado === "cuerpo" ? <FaCheck size={12} /> : <FaRegCopy size={12} />} {copiado === "cuerpo" ? "Copiado" : "Copiar cuerpo"}</button>
            <button type="button" onClick={abrirMail} style={btnSec}><FaExternalLinkAlt size={11} /> Abrir en el mail{mailTo ? "" : " (falta email)"}</button>
          </div>
        </>
      )}

      {/* Material de los planes para adjuntar/compartir */}
      <div style={{ display: "flex", alignItems: "center", gap: spacing[2], marginTop: spacing[3], flexWrap: "wrap" }}>
        <span style={{ fontSize: typography.sizes.sm, color: colors.gray600, display: "inline-flex", alignItems: "center", gap: 5 }}><FaRegFilePdf size={11} color={colors.error} /> Material de los planes:</span>
        <a href={PLANES_PDF} target="_blank" rel="noreferrer" style={btnMini}><FaExternalLinkAlt size={10} /> Abrir / descargar PDF</a>
      </div>
      <p style={{ margin: `${spacing[2]}px 0 0`, fontSize: typography.sizes.xs, color: colors.gray400 }}>
        Copiá el mensaje, adjuntá el PDF de los planes (Platform · Operations · Full Back Office) y enviá.
      </p>
    </div>
  );
}

const box = { padding: spacing[3], background: colors.gray50, border: `1px solid ${colors.gray200}`, borderRadius: radius.md };
const lbl = { display: "block", fontSize: typography.sizes.sm, color: colors.gray600, marginBottom: 4 };
const preview = { whiteSpace: "pre-wrap", background: colors.white, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, padding: spacing[3], fontSize: typography.sizes.sm, color: colors.gray700, lineHeight: 1.5 };
const acciones = { display: "flex", gap: spacing[2], marginTop: spacing[2], flexWrap: "wrap" };
const tabBtn = (active) => ({ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", border: `1px solid ${active ? colors.primary : colors.gray200}`, borderRadius: radius.sm, background: active ? colors.primaryLighter : colors.white, color: active ? colors.primary : colors.gray700, cursor: "pointer", fontSize: typography.sizes.sm, fontFamily: "inherit" });
const btnPri = { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", border: "none", borderRadius: radius.sm, background: colors.primary, color: colors.white, cursor: "pointer", fontSize: typography.sizes.sm, fontFamily: "inherit" };
const btnSec = { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, background: colors.white, color: colors.gray700, cursor: "pointer", fontSize: typography.sizes.sm, fontFamily: "inherit" };
const btnMini = { display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 10px", border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, background: colors.white, color: colors.gray700, cursor: "pointer", fontSize: typography.sizes.xs, fontFamily: "inherit", textDecoration: "none" };
